import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { useTranslation } from '../hooks/useTranslation';
import { saveRedirectUrl } from '../utils/redirectHelpers';

type CheckoutResponse = {
  success?: boolean;
  code?: string;
  status?: string;
  clientSecret?: string;
  slug?: string;
};

const WAITING_LINES = [
  'checkout.waiting_1',
  'checkout.waiting_2',
  'checkout.waiting_3',
  'checkout.waiting_4',
  'checkout.waiting_5',
  'checkout.waiting_6',
] as const;

function copy(translate: (key: string) => string, key: string, fallback: string): string {
  const value = translate(key);
  return value === key ? fallback : value;
}

function WaitingCard({ line }: { line: string }) {
  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl px-8 py-10 text-center" aria-busy="true">
      <div className="flex items-end justify-center gap-1.5 h-10 mb-6" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((bar) => (
          <span
            key={bar}
            className="w-1.5 h-10 origin-bottom rounded-sm bg-purple-400"
            style={{
              animation: 'checkout-meter 1.1s ease-in-out infinite',
              animationDelay: `${bar * 0.12}s`,
            }}
          />
        ))}
      </div>
      <p className="text-gray-200 text-lg">{line}</p>
      <style>{`@keyframes checkout-meter { 0%, 100% { transform: scaleY(0.25); } 50% { transform: scaleY(1); } }`}</style>
    </div>
  );
}

export default function Checkout() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [waitingIndex, setWaitingIndex] = useState(0);
  const [needsWithdrawal, setNeedsWithdrawal] = useState(false);
  const [withdrawalChecked, setWithdrawalChecked] = useState(false);
  const [acceptedWithdrawal, setAcceptedWithdrawal] = useState(false);
  const [settled, setSettled] = useState(false);
  const acceptedSlug = useRef<string | undefined>(undefined);
  const waiting = settled && !error && !clientSecret && !needsWithdrawal;

  useEffect(() => {
    if (!waiting) return;
    const timer = window.setInterval(() => {
      setWaitingIndex((index) => (index + 1) % WAITING_LINES.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [waiting]);

  useEffect(() => {
    let cancelled = false;
    const paying = acceptedSlug.current === slug;
    if (!paying) {
      setSettled(false);
      setNeedsWithdrawal(false);
      setError(null);
      setClientSecret(null);
    }
    setWaitingIndex(0);
    const destination = `/checkout/${slug}`;
    const token = localStorage.getItem('token');
    if (!token) {
      saveRedirectUrl(destination);
      navigate('/register', { replace: true });
      return;
    }

    const stay = () => {
      if (!cancelled) setSettled(true);
    };
    const start = async () => {
      const response = await fetch(`/api/checkout/create?lang=${currentLanguage}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          slug,
          ...(paying ? { euWithdrawalConsent: true } : {}),
        }),
      });
      const data: CheckoutResponse = await response.json();
      if (cancelled) return;
      if (response.status === 401) {
        saveRedirectUrl(destination);
        navigate('/register', { replace: true });
        return;
      }
      if (data.code === 'WITHDRAWAL_CONSENT_REQUIRED') {
        setNeedsWithdrawal(true);
        stay();
        return;
      }
      if (data.code === 'ALREADY_OWNED') {
        navigate(`/purchase/already-owned?slug=${encodeURIComponent(slug || '')}`, { replace: true });
        return;
      }
      if (!response.ok || !data.success) {
        setError(t('checkout.failed'));
        stay();
        return;
      }
      if (data.status === 'paid') {
        navigate(`/purchase/success?added=${encodeURIComponent(data.slug || slug || '')}`, { replace: true });
        return;
      }
      if (!data.clientSecret) {
        setError(t('checkout.failed'));
        stay();
        return;
      }
      const configResponse = await fetch('/api/checkout/config');
      const config = await configResponse.json();
      if (cancelled) return;
      if (!config.publishableKey) {
        setError(t('checkout.failed'));
        stay();
        return;
      }
      setStripePromise(loadStripe(config.publishableKey));
      setClientSecret(data.clientSecret);
      stay();
    };

    start().catch(() => {
      if (cancelled) return;
      setError(t('checkout.failed'));
      setSettled(true);
    });
    return () => {
      cancelled = true;
    };
  }, [slug, currentLanguage, navigate, t, acceptedWithdrawal]);

  if (!settled) {
    return <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-6 text-center">{t('checkout.title')}</h1>
          {error && <p className="text-red-300 text-center mb-6">{error}</p>}
          {needsWithdrawal && (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl px-8 py-10">
              <p className="text-gray-300 mb-6">
                {copy(t, 'checkout.refund_note', 'Digital plugins are delivered right away. A refund is for a download or playback problem, a duplicate purchase within 24 hours, or a product that is not what the page describes. Ask within 30 days at support@sonicmakers.com.')}
              </p>
              <p className="mb-6">
                <Link to="/terms" className="text-purple-300 underline">{copy(t, 'checkout.refund_policy', 'Refund policy')}</Link>
              </p>
              <label className="flex items-start gap-3 text-gray-200 mb-6">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={withdrawalChecked}
                  onChange={(event) => setWithdrawalChecked(event.target.checked)}
                />
                <span>
                  {copy(t, 'checkout.eu_withdrawal', 'I agree to receive this plugin immediately and give up the 14-day right to withdraw. Buyers in the EU have to confirm this before payment.')}
                </span>
              </label>
              <button
                type="button"
                disabled={!withdrawalChecked}
                onClick={() => {
                  acceptedSlug.current = slug;
                  setNeedsWithdrawal(false);
                  setSettled(true);
                  setAcceptedWithdrawal(true);
                }}
                className="px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white disabled:cursor-not-allowed disabled:bg-gray-700 disabled:text-gray-400 disabled:from-gray-700 disabled:to-gray-700"
              >
                {copy(t, 'checkout.eu_continue', 'Continue to payment')}
              </button>
            </div>
          )}
          {waiting && <WaitingCard line={t(WAITING_LINES[waitingIndex])} />}
          {stripePromise && clientSecret && (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </div>
  );
}
